/**
 * @lodex-pro/web-admin/features/abbreviations/hooks — Task 25.7 (DEC-118).
 *
 * Read the abbreviation-overview endpoints (/v1/platform/abbreviations/*) and set
 * abbreviations by REUSING the existing PATCH endpoints — tenants (Task 25.2) and
 * platform operators (Task 25.3); the set-mutations invalidate the overview keys.
 */
import { useQueryClient } from '@tanstack/react-query';
import {
  apiErrorReasonValue,
  useApiQuery,
  useApiMutation,
  useApiOpts,
} from '@lodex-pro/web-shared';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface AbbrevConflict {
  readonly scope: 'hauler' | 'operator';
  readonly abbreviation: string;
  readonly ids: readonly string[];
}

export interface AbbrevSummary {
  readonly haulers: { total: number; missingAbbreviation: number; hasAbbreviation: number };
  readonly operators: { totalActive: number; missingAbbreviation: number; hasAbbreviation: number };
  readonly conflicts: readonly AbbrevConflict[];
  readonly haulIdReady: boolean;
}

export interface AbbrevHauler {
  readonly id: string;
  readonly name: string;
  readonly haulerAbbreviation: string | null;
  readonly jobCount: number;
  readonly isActive: boolean;
}

export interface AbbrevOperator {
  readonly id: string;
  readonly name: string;
  readonly displayName: string | null;
  readonly abbreviation: string | null;
  readonly haulerCount: number;
  readonly wellCount: number;
}

// ─── Query keys ──────────────────────────────────────────────────────────────

export const ABBREV_KEY = ['platform', 'abbreviations'] as const;
const SUMMARY_KEY = [...ABBREV_KEY, 'summary'];
const HAULERS_KEY = [...ABBREV_KEY, 'haulers'];
const OPERATORS_KEY = [...ABBREV_KEY, 'operators'];

// ─── Queries ─────────────────────────────────────────────────────────────────

export function useAbbrevSummary(): ReturnType<typeof useApiQuery<AbbrevSummary>> {
  const opts = useApiOpts();
  return useApiQuery<AbbrevSummary>(opts, '/v1/platform/abbreviations/summary', {
    queryKey: SUMMARY_KEY,
    staleTime: 15_000,
  });
}

export function useAbbrevHaulers(): ReturnType<
  typeof useApiQuery<{ haulers: readonly AbbrevHauler[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ haulers: readonly AbbrevHauler[] }>(
    opts,
    '/v1/platform/abbreviations/haulers',
    { queryKey: HAULERS_KEY, staleTime: 15_000 },
  );
}

export function useAbbrevOperators(): ReturnType<
  typeof useApiQuery<{ operators: readonly AbbrevOperator[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ operators: readonly AbbrevOperator[] }>(
    opts,
    '/v1/platform/abbreviations/operators',
    { queryKey: OPERATORS_KEY, staleTime: 15_000 },
  );
}

// ─── Mutations (reuse the Task 25.2 / 25.3 PATCH endpoints) ──────────────────

function useInvalidateAll(): () => void {
  const qc = useQueryClient();
  return () => void qc.invalidateQueries({ queryKey: ABBREV_KEY });
}

export function useSetHaulerAbbreviation(
  tenantId: string,
): ReturnType<typeof useApiMutation<{ haulerAbbreviation: string }, unknown>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateAll();
  return useApiMutation<{ haulerAbbreviation: string }, unknown>(
    opts,
    `/v1/platform/tenants/${tenantId}`,
    'PATCH',
    { onSuccess: invalidate },
  );
}

export function useSetOperatorAbbreviation(
  operatorId: string,
): ReturnType<typeof useApiMutation<{ abbreviation: string }, unknown>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateAll();
  return useApiMutation<{ abbreviation: string }, unknown>(
    opts,
    `/v1/platform/operators/${operatorId}`,
    'PATCH',
    { onSuccess: invalidate },
  );
}

/** 0205 — customers of one hauler that print the same ticket code (their HaulIds can collide). */
export interface TicketPrefixCollision {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly prefix: string;
  readonly customers: ReadonlyArray<{
    readonly id: string;
    readonly name: string;
    readonly isPlatformOperator: boolean;
    readonly isActive: boolean;
    readonly tickets: number;
  }>;
}

export function useTicketPrefixCollisions(): ReturnType<
  typeof useApiQuery<{ collisions: readonly TicketPrefixCollision[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ collisions: readonly TicketPrefixCollision[] }>(
    opts,
    '/v1/platform/abbreviations/collisions',
    { queryKey: [...ABBREV_KEY, 'collisions'], staleTime: 15_000 },
  );
}

/**
 * A Paelode change refused by 0205: which hauler, the customer whose ticket code it would move
 * (with the operator it is on, e.g. an earlier alias of a merge source) and the customer that
 * already prints that code. From the 409's field reasons; any piece can be missing.
 */
export interface TicketCodeCollision {
  readonly hauler: string | null;
  readonly moving: string | null;
  readonly other: string | null;
}

export function ticketCodeCollision(err: unknown): TicketCodeCollision | null {
  const code = typeof err === 'object' && err !== null ? (err as { code?: unknown }).code : null;
  if (code !== 'PLATFORM_ABBREVIATION_COLLIDES') return null;
  // "<hauler>: <customer> (on <operator>)" and "<hauler>: <customer>" (or just "<customer>").
  const moving = apiErrorReasonValue(err, 'ticket_prefix_moving');
  const taken = apiErrorReasonValue(err, 'ticket_prefix_taken_by');
  const cut = (s: string | null): string | null =>
    s === null || !s.includes(': ') ? null : s.slice(0, s.indexOf(': '));
  const hauler = cut(moving) ?? cut(taken);
  const strip = (s: string | null): string | null =>
    s === null
      ? null
      : hauler !== null && s.startsWith(`${hauler}: `)
        ? s.slice(hauler.length + 2)
        : s;
  return { hauler, moving: strip(moving), other: strip(taken) };
}

/** The collision in a sentence, or null when the 409 named nobody. */
function collisionSentence(c: TicketCodeCollision, change: string): string | null {
  if (c.other === null) return null;
  const at = c.hauler === null ? 'a hauler' : c.hauler;
  return c.moving === null
    ? `${change} would give ${c.other} at ${at} a ticket code another of its customers prints.`
    : `${change} would make ${c.moving} print the same ticket code as ${c.other}, at ${at}.`;
}

/** What a refused abbreviation save means, in words. */
export function abbrevSaveError(err: unknown): string {
  const c = ticketCodeCollision(err);
  if (c !== null) {
    const said = collisionSentence(c, 'This code');
    return `${said ?? 'Two customers of a hauler would print this code on their tickets.'} See Ticket code collisions below, choose another code, or have the hauler change one customer’s code first.`;
  }
  return 'Could not save — may already be taken.';
}

/**
 * What a refused merge means, in words. A customer still on an earlier alias of the source moves
 * with it, so the 409 names the operator it is on. (Folding aliases through more gently is a
 * later item.)
 */
export function mergeError(err: unknown): string {
  const c = ticketCodeCollision(err);
  if (c === null) return 'Could not merge operators';
  const said = collisionSentence(c, 'This merge');
  return `${said ?? 'This merge would give two customers of a hauler the same ticket code.'} Have ${c.hauler ?? 'the hauler'} change one of those customers’ codes, or change a Paelode abbreviation, then merge again.`;
}
